/**
 * Durations, elapsed time and relative time.
 *
 * Minutes are the unit everywhere — the wire carries 27 `*Minutes` fields
 * and no seconds, millis or ISO-8601 durations, so nothing here converts
 * units, it only renders them. Clock arithmetic is the one exception and
 * it is confined to {@link elapsedMinutes}.
 *
 * Before this module the same minutes value rendered six different ways
 * (`3h 20m`, `3 h 20`, `1.5h`, `3 Std. 20 Min.`, `200 Min.`, `2d 3h 04m`).
 * The canonical compact shape is `3h 20m`.
 */
import type { Lang } from '../i18n/lang'

export interface DurationOptions {
  /**
   * Roll hours into days past 24h — `2d 3h 4m` instead of `51h 4m`. Off by
   * default; a work order paused overnight is the case that wants it.
   */
  readonly days?: boolean
}

/**
 * Minutes as a shop reads them: `45m`, `3h`, `3h 20m`.
 *
 * Never zero-pads. A padded `1h 00m` and a bare `1h` are the same duration
 * and having both in the tree is exactly the drift this replaces.
 */
export function formatDuration(totalMinutes: number, opts: DurationOptions = {}): string {
  const total = Math.max(0, Math.trunc(totalMinutes))
  if (opts.days && total >= 1440) {
    const d = Math.floor(total / 1440)
    const h = Math.floor((total % 1440) / 60)
    const m = total % 60
    return [`${d}d`, h > 0 ? `${h}h` : null, m > 0 ? `${m}m` : null].filter(Boolean).join(' ')
  }
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

const LONG_UNITS: Record<Lang, { h: string; m: string }> = {
  de: { h: 'Std.', m: 'Min.' },
  en: { h: 'h', m: 'min' },
}

/**
 * Spelled out for customer-facing copy: `1 Std. 30 Min.` / `1 h 30 min`.
 * Hours are only named once there is a whole one (`45 Min.`). The same
 * shape the backend's `DurationLabel` prints on the invoice and the job
 * agreement — an Arbeitszeit line's Menge reads identically on screen and
 * on paper. (English was "hrs" until 2026-09-24; "1 hrs" is not English.)
 */
export function formatDurationLong(totalMinutes: number, lang: Lang): string {
  const total = Math.max(0, Math.trunc(totalMinutes))
  const u = LONG_UNITS[lang] ?? LONG_UNITS.de
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} ${u.m}`
  if (m === 0) return `${h} ${u.h}`
  return `${h} ${u.h} ${m} ${u.m}`
}

/** `3:20` — hours and zero-padded minutes, for time-entry fields. */
export function minutesToClock(totalMinutes: number): string {
  const total = Math.max(0, Math.trunc(totalMinutes))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/**
 * Parse a typed duration to minutes. Accepts `1:30`, `1,5`, `1.5` and `90`.
 *
 * A bare integer is read as **minutes**, not hours. The old management
 * parser read `90` as 90 hours and leaned on a plausibility prompt to
 * catch it; reading the same digits as the unit everything else uses is
 * the less surprising rule.
 */
export function parseDurationInput(raw: string): number | null {
  const s = raw.trim().replace(',', '.')
  if (s === '') return null
  const clock = /^(\d+):([0-5]?\d)$/.exec(s)
  if (clock) return Number(clock[1]) * 60 + Number(clock[2])
  if (!/^\d*\.?\d+$/.test(s)) return null
  const n = Number(s)
  if (!Number.isFinite(n)) return null
  return Number.isInteger(n) ? n : Math.round(n * 60)
}

/**
 * Minutes elapsed since an instant. `now` is a parameter, never a call to
 * the clock inside — that is what makes the callers testable and lets a
 * ticking component share one `now` across a whole render.
 */
export function elapsedMinutes(sinceIso: string | null | undefined, nowMs: number): number {
  if (!sinceIso) return 0
  const started = new Date(sinceIso).getTime()
  if (Number.isNaN(started)) return 0
  return Math.max(0, Math.floor((nowMs - started) / 60_000))
}

/* ------------------------------------------------------------------ */
/* Relative time                                                       */
/* ------------------------------------------------------------------ */

export type RelativeUnit = 'now' | 'minutes' | 'hours' | 'days'

export interface RelativeTime {
  readonly unit: RelativeUnit
  readonly n: number
  /** True when the instant is ahead of `now` — "in 5m" rather than "5m ago". */
  readonly future: boolean
}

/**
 * Bucket a signed delta into a unit and a count.
 *
 * Floors at every step. The previous copies rounded, so 23h30m crossed
 * into the day bucket and rendered as "1 d" — a half-day error on an age
 * column.
 */
export function relativeTime(deltaMs: number): RelativeTime {
  const future = deltaMs > 0
  const min = Math.floor(Math.abs(deltaMs) / 60_000)
  if (min < 1) return { unit: 'now', n: 0, future }
  if (min < 60) return { unit: 'minutes', n: min, future }
  const h = Math.floor(min / 60)
  if (h < 24) return { unit: 'hours', n: h, future }
  return { unit: 'days', n: Math.floor(h / 24), future }
}

/** Translation keys for one feature's relative-time column. */
export interface RelativeTimeKeys<K extends string = string> {
  readonly now: K
  readonly minutes: K
  readonly hours: K
  readonly days: K
  /** Future-tense variants. Omit where a column is only ever historical. */
  readonly inMinutes?: K
  readonly inHours?: K
  readonly inDays?: K
}

/**
 * Render a delta through a feature's own translation keys.
 *
 * The keys stay per-feature — `tickets.ageHours` and `appointments.inHours`
 * are genuinely different strings — while the thresholds, the rounding and
 * the direction handling are shared.
 */
export function formatRelativeTime<K extends string>(
  deltaMs: number,
  t: (key: K, vars?: Readonly<Record<string, string | number>>) => string,
  keys: RelativeTimeKeys<K>,
): string {
  const { unit, n, future } = relativeTime(deltaMs)
  if (unit === 'now') return t(keys.now)
  const key =
    unit === 'minutes' ? (future && keys.inMinutes) || keys.minutes
    : unit === 'hours' ? (future && keys.inHours) || keys.hours
    : (future && keys.inDays) || keys.days
  return t(key, { n })
}
